import clsx from 'clsx';
import * as React from 'react';

import {
    BLOCK_CODE,
    BLOCK_EMBED,
    BLOCK_IMAGES,
    BLOCK_LIST_UL,
    BLOCK_REUSABLE_CONTENT,
    canInsertBlockIn,
    code,
    images,
    list,
} from '@gitbook/doc-core';
import { createPalette, type PaletteDef } from '@gitbook/react-uikit';
import { Block, BlockRange, TextRange } from '@gitbook/slate';
import { type EditorContainer, useEditor, useIsEditorComposing } from '@gitbook/slate-react';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    KeyboardShortcut,
    Toolbar,
    type ToolbarItemProps,
    type ToolbarRef,
} from '@gitbook/spine-react';
import { useEventCallback } from '@gitbook/util-react-hooks';

import { shortcuts } from '../../../Keyboard';
import { useInsertFiles } from '../../InsertFilesDialog';
import {
    type BlockControlsRef,
    BlockWrapper,
    type BlockWrapperProps,
    insertBlockInBlock,
} from '../common';
import { useCompletionApi } from '../completion';
import { type BlockVariant, BlockVariantContext, ContentEditorContext } from '../contexts';
import { useReusableContentPaletteCommandsFactory } from '../reusable-content';

const blockVariantClassName: Record<BlockVariant, string> = {
    caption: 'text-ui-small text-muted',
    expandableTitle: 'text-ui-heading-small leading-6',
    quote: 'text-content-paragraph text-muted',
    root: 'text-content-paragraph',
    hint: 'text-content-paragraph-small',
};

function getInstructions(options: {
    canUseAI: boolean;
    canInsertEmbeds: boolean;
    reusableContent: boolean;
}) {
    const parts = [
        <React.Fragment key="commands">
            Type <KeyboardShortcut className="inline-flex" shortcut={shortcuts.EDITOR_PALETTE} /> to
            insert a block
        </React.Fragment>,
    ];
    if (options.canUseAI) {
        parts.push(
            <React.Fragment key="ai">
                <KeyboardShortcut className="inline-flex" shortcut="Space" /> for AI
            </React.Fragment>
        );
    }
    if (options.reusableContent) {
        parts.push(
            <React.Fragment key="reusable-content">
                <KeyboardShortcut className="inline-flex" shortcut="<" /> for reusable content
            </React.Fragment>
        );
    }
    if (!options.canUseAI && !options.reusableContent && options.canInsertEmbeds) {
        parts.push(<React.Fragment key="embed">paste a link to embed content</React.Fragment>);
    }
    return parts.map((part, index) => (
        <React.Fragment key={part.key}>
            {part}
            {index === parts.length - 2 ? ' or ' : index < parts.length - 1 ? ', ' : ''}
        </React.Fragment>
    ));
}

/**
 * Wrapper to show insert commands.
 */
export function InsertBlockWrapper(
    props: BlockWrapperProps & {
        /** Class name to apply to the block wrapper. */
        className?: string;

        /** Placeholder text to display, even when not focused */
        placeholder?: string | null;

        /** Palette to use for this block. */
        palette?: () => PaletteDef;
    }
): React.ReactElement {
    const { node, parent, editor, children, placeholder } = props;
    const { selection } = editor.value;
    const isFocused = TextRange.isTextRange(selection) && selection.hasOnlyNodeFocused(node);
    const blockWrapperRef = React.useRef<BlockControlsRef | null>(null);
    const toolbarRef = React.useRef<ToolbarRef>(null);
    const isComposing = useIsEditorComposing();

    const capabilities = {
        embeds: canInsertBlockIn(parent, BLOCK_EMBED),
    };
    return (
        <BlockWrapper ref={blockWrapperRef} {...props}>
            <div
                contentEditable={false}
                className={clsx(
                    '@container pointer-events-none absolute flex w-full items-center justify-between',
                    isFocused || placeholder ? 'opacity-100' : 'opacity-0'
                )}
            >
                {isFocused && !isComposing ? (
                    <>
                        <Shortcuts
                            node={props.node}
                            blockWrapperRef={blockWrapperRef}
                            toolbarRef={toolbarRef}
                        />
                        <Placeholder>
                            <Instructions capabilities={capabilities} />
                        </Placeholder>
                        <BlockWrapperToolbar ref={toolbarRef} nodeKey={node.key} />
                    </>
                ) : (
                    <Placeholder>{placeholder}</Placeholder>
                )}
            </div>
            {children}
        </BlockWrapper>
    );
}

const Shortcuts = React.memo(function Shortcuts(props: {
    node: Block;
    blockWrapperRef: React.RefObject<BlockControlsRef>;
    toolbarRef: React.RefObject<ToolbarRef>;
}) {
    const { node, blockWrapperRef, toolbarRef } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    const editor = useEditor();
    const completionApi = useCompletionApi();

    const createReusableContentCommands = useReusableContentPaletteCommandsFactory({
        onSelectReusableContent: (block, ref) => {
            // Replace the current block with the reusable content block.
            editor.change((change) => {
                change.insertBlock(block).removeNodeByKey(node.key);
                // Collapse the selection to the end of the block and focus.
                change
                    .select(
                        TextRange.fromBlockRange(
                            BlockRange.createAtBlock(block),
                            change.value.document
                        )
                    )
                    .collapseToEndOfBlock()
                    .focus();
            });
            logAnalyticsEvent({
                type: 'edit_page_document_insert_block',
                from: 'quick-insert',
                blockType: BLOCK_REUSABLE_CONTENT,
                reusableContentId: ref.reusableContent,
            });
        },
    });

    // Listen to keyboard events to intercept Tab/Space before Slate
    useListenKeydown((event) => {
        // Disable shortcuts when composing text.
        if (editor.isComposing) {
            return;
        }

        if (editor.value.isBlurred) {
            return;
        }

        // If the event target is not inside the editor, ignore it.
        // A fragment handles its own events so will not have the `editor.elementRef` as its target.
        const editorRef = editor.elementRef?.current;
        if (editorRef && event.target && !editorRef.contains(event.target as Node)) {
            return;
        }

        // When / is pressed, open the command palette.
        if (event.key === shortcuts.EDITOR_PALETTE) {
            event.preventDefault();
            blockWrapperRef.current?.openCommandPalette();
        }

        // When tab is pressed, focus the toolbar.
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            toolbarRef.current?.focus();
        }

        // When space is pressed, insert the completion.
        if (completionApi && event.key === ' ') {
            event.preventDefault();
            completionApi.open({ trigger: 'keyboard-space' });
        }

        // When "<" is pressed, insert a reusable content.
        if (createReusableContentCommands && event.key === '<') {
            event.preventDefault();
            blockWrapperRef.current?.openCommandPalette(
                createPalette({
                    search: { placeholder: 'Search reusable content…' },
                    groups: [
                        {
                            key: 'reusable-content',
                            title: 'Reusable content',
                            commands: createReusableContentCommands(),
                        },
                    ],
                })
            );
        }
    });

    return null;
});

const BlockWrapperToolbar = React.memo(
    React.forwardRef(function BlockWrapperToolbar(
        props: { nodeKey: string },
        ref: React.ForwardedRef<ToolbarRef>
    ) {
        const editor = useEditor();
        return (
            <Toolbar
                ref={ref}
                unstyled
                size="small"
                className="pointer-events-auto shrink-0 gap-2 supports-[container-type:inline-size]:@[12rem]:flex supports-[container-type:inline-size]:hidden"
                onKeyDownCapture={(event) => {
                    if (event.key === 'Tab' || event.key === 'Escape') {
                        event.preventDefault();
                        editor.elementRef?.current?.focus();
                    }
                }}
            >
                <WriteWithAIButton />
                <ImageButton nodeKey={props.nodeKey} editor={editor} />
                <CodeBlockButton nodeKey={props.nodeKey} editor={editor} />
                <ListButton nodeKey={props.nodeKey} editor={editor} />
            </Toolbar>
        );
    })
);

function ListButton(props: ToolbarItemProps & { nodeKey: string; editor: EditorContainer }) {
    const { nodeKey, editor, ...toolbarItemProps } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    return (
        <Button
            {...toolbarItemProps}
            label="Unordered list"
            kind="secondary"
            leadingIcon={Icon.ListUnordered}
            onPress={() => {
                editor.change((change) => {
                    insertBlockInBlock(
                        change,
                        nodeKey,
                        list.createList(BLOCK_LIST_UL),
                        'replace',
                        'end'
                    );
                });
                logAnalyticsEvent({
                    type: 'edit_page_document_insert_block',
                    from: 'quick-insert',
                    blockType: BLOCK_LIST_UL,
                });
            }}
        />
    );
}

function CodeBlockButton(props: ToolbarItemProps & { nodeKey: string; editor: EditorContainer }) {
    const { nodeKey, editor, ...toolbarItemProps } = props;
    const { logAnalyticsEvent } = React.useContext(ContentEditorContext);
    return (
        <Button
            {...toolbarItemProps}
            label="Code block"
            kind="secondary"
            leadingIcon={Icon.Code}
            onPress={() => {
                editor.change((change) => {
                    insertBlockInBlock(
                        change,
                        nodeKey,
                        code.createCodeBlock('// Some code'),
                        'replace',
                        'end'
                    );
                });
                logAnalyticsEvent({
                    type: 'edit_page_document_insert_block',
                    from: 'quick-insert',
                    blockType: BLOCK_CODE,
                });
            }}
        />
    );
}

function ImageButton(props: ToolbarItemProps & { nodeKey: string; editor: EditorContainer }) {
    const { nodeKey, editor, ...toolbarItemProps } = props;
    const {
        logAnalyticsEvent,
        blocks: { withImages },
    } = React.useContext(ContentEditorContext);
    const insertFiles = useInsertFiles();
    if (!withImages) {
        return null;
    }
    return (
        <Button
            {...toolbarItemProps}
            label="Image"
            kind="secondary"
            leadingIcon={Icon.Image}
            onPress={() => {
                insertFiles
                    .prompt({
                        title: 'Select images',
                        accept: 'images',
                        acceptURL: true,
                    })
                    .then((selectedFiles) => {
                        if (selectedFiles.length === 0) {
                            return;
                        }
                        editor.change((change) => {
                            insertBlockInBlock(
                                change,
                                nodeKey,
                                Block.create({
                                    type: BLOCK_IMAGES,
                                    isVoid: true,
                                    nodes: selectedFiles.map((selectedFile) => {
                                        return images.createImageBlock(selectedFile);
                                    }),
                                }),
                                'replace',
                                'end'
                            );
                        });
                        logAnalyticsEvent({
                            type: 'edit_page_document_insert_block',
                            from: 'quick-insert',
                            blockType: BLOCK_IMAGES,
                            images: selectedFiles.length,
                        });
                    });
            }}
        />
    );
}

function WriteWithAIButton(props: ToolbarItemProps) {
    const completionApi = useCompletionApi();
    if (!completionApi) {
        return null;
    }
    return (
        <Button
            {...props}
            label="Write with AI"
            kind="secondary"
            leadingIcon={Icon.Magic}
            onPress={() => {
                completionApi.open({ trigger: 'block-button' });
            }}
        />
    );
}

function Placeholder(props: { children: React.ReactNode }) {
    const blockVariant = React.useContext(BlockVariantContext);
    return (
        <div
            className={clsx(
                'mr-2 inline-block truncate whitespace-nowrap text-muted',
                blockVariantClassName[blockVariant]
            )}
        >
            {props.children}
        </div>
    );
}

const Instructions = React.memo(function Instructions(props: {
    capabilities: { embeds: boolean };
}) {
    const {
        blocks: { withCompletions, withReusableContent },
    } = React.useContext(ContentEditorContext);
    return (
        <>
            {getInstructions({
                canUseAI: !!withCompletions,
                reusableContent: !!withReusableContent,
                canInsertEmbeds: props.capabilities.embeds,
            })}
        </>
    );
});

function useListenKeydown(listener: ((event: KeyboardEvent) => void) | null) {
    const stableListener = useEventCallback((event: KeyboardEvent) => {
        listener?.(event);
    });
    const handler = listener ? stableListener : null;
    React.useEffect(() => {
        if (!handler) {
            return;
        }

        document.addEventListener('keydown', handler);
        return () => {
            document.removeEventListener('keydown', handler);
        };
    }, [handler]);
}
